<template>
  <section class="apps-section central-costs-wrapper">
    <div class="section-header admin-header justify-center">
      <div>
        <p class="section-label">Administração</p>
        <h3>Centros de Custo</h3>
      </div>

      <div class="admin-header-actions flex-grow-1 justify-end">
        <v-text-field
          v-model="search"
          label="Pesquisar"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 280px;"
          @update:model-value="onSearchChange"
        />

        <v-btn
          color="primary"
          icon
          :loading="loading"
          @click="refreshList"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-card class="admin-card">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="centralCostsList"
        :items-length="totalCentralCosts"
        :loading="loading"
        :items-per-page-options="[5, 10, 15, 20, 25]"
        item-value="id"
        @update:options="loadCentralCostsList"
      >
        <template #item.central_cost="{ item }">
          {{ item.central_cost || '-' }}
        </template>

        <template #item.department="{ item }">
          {{ item.department || '-' }}
        </template>

        <template #item.actions="{ item }">
          <div class="row-actions">
            <v-tooltip text="Associar users">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  color="primary"
                  @click="openUsersDialog(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>

        <template #no-data>
          <div class="empty-state">
            <div class="empty-icon">i</div>
            <h4>Sem resultados</h4>
            <p>Nenhum centro de custo encontrado.</p>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <v-dialog v-model="showUsersDialog" max-width="900">
      <v-card class="dialog-card">
        <v-card-title>
          Associar users ao centro de custo
        </v-card-title>

        <v-card-text>
          <div class="selected-central-cost">
            <strong>Centro de Custo:</strong>
            {{ selectedCentralCost?.central_cost || '-' }}

            <span v-if="selectedCentralCost?.department">
              —
              {{ selectedCentralCost.department }}
            </span>
          </div>

          <div v-if="usersSubmitError" class="submit-error">
            {{ usersSubmitError }}
          </div>

          <v-text-field
            v-model="usersSearch"
            label="Pesquisar users por nome ou código"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            class="users-search"
          />

          <v-data-table
            :headers="usersHeaders"
            :items="filteredUsers"
            :loading="loadingUsers"
            item-value="id"
            density="compact"
            class="users-table"
            :items-per-page="10"
          >
            <template #item.selected="{ item }">
              <v-checkbox-btn
                :model-value="isUserSelected(item.id)"
                :disabled="savingUsers"
                @update:model-value="toggleUser(item.id)"
              />
            </template>

            <template #item.funcionario_codigo="{ item }">
              {{ getUserCode(item) }}
            </template>

            <template #item.name="{ item }">
              {{ getUserName(item) }}
            </template>

            <template #item.email="{ item }">
              {{ item.email || '-' }}
            </template>

            <template #no-data>
              <div class="empty-state users-empty-state">
                <h4>Sem users</h4>
                <p>Nenhum user encontrado.</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            :disabled="savingUsers"
            @click="closeUsersDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="savingUsers"
            :disabled="savingUsers || loadingUsers"
            @click="saveUsers"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import CentralCosts from "@/api/CentralCosts";
import Users from "@/api/Users";

defineOptions({
    name: "CentralCostsTable",
});

const loading = ref(false);
const centralCostsList = ref([]);
const totalCentralCosts = ref(0);
const page = ref(1);
const itemsPerPage = ref(10);
const search = ref("");

const showUsersDialog = ref(false);
const selectedCentralCost = ref(null);
const loadingUsers = ref(false);
const savingUsers = ref(false);
const usersSubmitError = ref("");
const users = ref([]);
const selectedUserIds = ref([]);
const usersSearch = ref("");

const headers = [
    {
        title: "Centro de Custo",
        key: "central_cost",
    },
    {
        title: "Departamento",
        key: "department",
    },
    {
        title: "Ações",
        key: "actions",
        sortable: false,
        width: 100,
    },
];

const usersHeaders = [
    {
        title: "",
        key: "selected",
        sortable: false,
        width: 60,
    },
    {
        title: "Código",
        key: "funcionario_codigo",
        width: 120,
    },
    {
        title: "Nome",
        key: "name",
    },
    {
        title: "Email",
        key: "email",
    },
];

const filteredUsers = computed(() => {
    const term = usersSearch.value?.trim()?.toLowerCase();

    if (!term) {
        return users.value;
    }

    return users.value.filter((user) => {
        const name = getUserName(user).toLowerCase();
        const email = String(user.email || "").toLowerCase();
        const username = String(user.username || "").toLowerCase();
        const funcionarioCodigo = getUserCode(user).toLowerCase();

        return name.includes(term)
            || email.includes(term)
            || username.includes(term)
            || funcionarioCodigo.includes(term);
    });
});

onMounted(() => {
    loadCentralCostsList({
        page: page.value,
        itemsPerPage: itemsPerPage.value,
    });
});

async function loadCentralCostsList(options = {}) {
    loading.value = true;

    try {
        const nextPage = options.page || page.value;
        const nextItemsPerPage = options.itemsPerPage || itemsPerPage.value;

        page.value = nextPage;
        itemsPerPage.value = nextItemsPerPage;

        const response = await CentralCosts.search({
            page: page.value,
            limit: itemsPerPage.value,
            search: search.value || "",
        });

        centralCostsList.value = normalizeItems(response?.data);
        totalCentralCosts.value = normalizeTotal(response?.data, centralCostsList.value.length);
    } catch (error) {
        console.error("Erro ao carregar centros de custo:", error?.response?.data || error);

        centralCostsList.value = [];
        totalCentralCosts.value = 0;
    } finally {
        loading.value = false;
    }
}

function normalizeItems(payload) {
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
}

function normalizeTotal(payload, fallback = 0) {
    if (typeof payload?.total === "number") {
        return payload.total;
    }

    if (typeof payload?.meta?.total === "number") {
        return payload.meta.total;
    }

    return fallback;
}

function onSearchChange() {
    page.value = 1;

    loadCentralCostsList({
        page: 1,
        itemsPerPage: itemsPerPage.value,
    });
}

function refreshList() {
    loadCentralCostsList({
        page: page.value,
        itemsPerPage: itemsPerPage.value,
    });
}

async function openUsersDialog(centralCost) {
    selectedCentralCost.value = centralCost;
    showUsersDialog.value = true;
    usersSubmitError.value = "";
    usersSearch.value = "";
    users.value = [];
    selectedUserIds.value = [];

    await loadUsersForCentralCost(centralCost.id);
}

async function loadUsersForCentralCost(centralCostId) {
    loadingUsers.value = true;

    try {
        const [usersResponse, selectedResponse] = await Promise.all([
            Users.all(),
            CentralCosts.users(centralCostId),
        ]);

        users.value = normalizeUsers(usersResponse?.data);

        selectedUserIds.value = Array.isArray(selectedResponse?.data?.user_ids)
            ? selectedResponse.data.user_ids
            : [];
    } catch (error) {
        usersSubmitError.value = "Ocorreu um erro ao carregar os users.";
        console.error("Erro ao carregar users do centro de custo:", error?.response?.data || error);
    } finally {
        loadingUsers.value = false;
    }
}

function normalizeUsers(payload) {
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
}

function getUserName(user) {
    return user?.name
        || user?.full_name
        || user?.display_name
        || user?.username
        || user?.email
        || "-";
}

function getUserCode(user) {
    return String(
        user?.funcionario_codigo
        || user?.worker?.code
        || user?.code
        || "-"
    );
}

function isUserSelected(userId) {
    return selectedUserIds.value.includes(userId);
}

function toggleUser(userId) {
    if (!userId) {
        return;
    }

    if (isUserSelected(userId)) {
        selectedUserIds.value = selectedUserIds.value.filter((id) => id !== userId);
        return;
    }

    selectedUserIds.value = [
        ...selectedUserIds.value,
        userId,
    ];
}

function closeUsersDialog() {
    showUsersDialog.value = false;
    selectedCentralCost.value = null;
    usersSubmitError.value = "";
    usersSearch.value = "";
    users.value = [];
    selectedUserIds.value = [];
}

async function saveUsers() {
    if (!selectedCentralCost.value?.id) {
        return;
    }

    savingUsers.value = true;
    usersSubmitError.value = "";

    try {
        await CentralCosts.syncUsers(selectedCentralCost.value.id, {
            user_ids: selectedUserIds.value,
        });

        closeUsersDialog();
    } catch (error) {
        usersSubmitError.value = error?.response?.data?.message || "Ocorreu um erro ao guardar os users.";
        console.error("Erro ao guardar users do centro de custo:", error?.response?.data || error);
    } finally {
        savingUsers.value = false;
    }
}
</script>

<style scoped>
.central-costs-wrapper {
  margin-top: 32px;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.admin-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.dialog-card {
  border-radius: 18px;
}

.selected-central-cost {
  margin-bottom: 16px;
  color: #334155;
}

.users-search {
  margin-bottom: 16px;
}

.users-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.submit-error {
  margin-bottom: 12px;
  color: #dc2626;
  font-size: 0.95rem;
}

.empty-state {
  text-align: center;
  padding: 40px;
}

.users-empty-state {
  padding: 24px;
}

.empty-icon {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.v-data-table,
.v-table,
.v-data-table-header,
.v-data-footer {
  background: transparent !important;
}

.v-data-table th {
  font-weight: 600;
  color: #1e293b;
}

.v-data-table td {
  color: #475569;
}
</style>